import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import authService from '../services/authService';
import { validateEmail, validatePassword, validateNickname } from '../utils/validation';
import { useToast } from '../contexts/ToastContext';
import Icon from './Icon';
import {
  TERMS_OF_SERVICE,
  PERSONAL_INFO_AGREEMENT,
  MARKETING_CONSENT,
  UNDER_14_NOTICE
} from '../constants/terms';

/**
 * 비밀번호 보안 요구사항 체크리스트 컴포넌트
 */
const PasswordChecklist = ({ password }) => {
  const checks = useMemo(() => [
    { key: 'minLength', label: '8자 이상', valid: password.length >= 8 },
    { key: 'uppercase', label: '대문자 포함', valid: /[A-Z]/.test(password) },
    { key: 'lowercase', label: '소문자 포함', valid: /[a-z]/.test(password) },
    { key: 'number', label: '숫자 포함', valid: /\d/.test(password) },
    { key: 'special', label: '특수문자 포함 (@$!%*?&)', valid: /[@$!%*?&]/.test(password) },
  ], [password]);

  return (
    <div className="password-checklist" role="list" aria-label="비밀번호 보안 요구사항">
      {checks.map((check) => (
        <div
          key={check.key}
          className="password-checklist__item"
          role="listitem"
          aria-label={`${check.label}: ${check.valid ? '충족' : '미충족'}`}
        >
          <span
            className={`password-checklist__icon ${check.valid ? 'password-checklist__icon--valid' : ''}`}
          >
            {check.valid ? '\u2713' : '\u25CB'}
          </span>
          <span>{check.label}</span>
        </div>
      ))}
    </div>
  );
};

PasswordChecklist.propTypes = {
  password: PropTypes.string.isRequired,
};

/**
 * 약관 모달 컴포넌트
 */
const TermsModal = ({ title, content, onClose }) => {
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  return (
    <div
      className="terms-modal-overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      tabIndex={-1}
    >
      <div className="terms-modal">
        <h2 id="terms-modal-title" className="terms-modal__title">{title}</h2>
        <div className="terms-modal__content">
          <pre className="terms-modal__text">{content}</pre>
        </div>
        <button
          className="btn-primary btn-full-width"
          onClick={onClose}
          autoFocus
        >
          확인
        </button>
      </div>
    </div>
  );
};

TermsModal.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

function Register() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    email: '',
    nickname: '',
    password: '',
    confirmPassword: '',
    birthdate: '',
    gender: '',
  });
  const [error, setError] = useState('');
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // 약관 동의 상태
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [creditInfoAgreed, setCreditInfoAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [ageAgreed, setAgeAgreed] = useState(false);

  // 약관 모달 상태
  const [termsModal, setTermsModal] = useState({ open: false, title: '', content: '' });

  const allAgreed = termsAgreed && creditInfoAgreed && marketingAgreed && ageAgreed;
  const requiredTermsAgreed = termsAgreed && creditInfoAgreed && ageAgreed;

  const handleAllAgreeToggle = useCallback(() => {
    const newValue = !allAgreed;
    setTermsAgreed(newValue);
    setCreditInfoAgreed(newValue);
    setMarketingAgreed(newValue);
    setAgeAgreed(newValue);
  }, [allAgreed]);

  const openTermsModal = useCallback((title, content) => {
    setTermsModal({ open: true, title, content });
  }, []);

  const closeTermsModal = useCallback(() => {
    setTermsModal({ open: false, title: '', content: '' });
  }, []);

  const handleGenderSelect = useCallback((gender) => {
    setFormData((prev) => ({ ...prev, gender }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // 전체 유효성 검사
    const emailError = validateEmail(formData.email);
    const nicknameError = validateNickname(formData.nickname);
    const passwordError = validatePassword(formData.password);

    if (emailError || nicknameError || passwordError) {
      setError('입력 정보를 다시 확인해주세요.');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      setIsLoading(false);
      return;
    }

    // 필수 약관 동의 확인
    if (!requiredTermsAgreed) {
      setError('필수 약관에 모두 동의해주세요.');
      setIsLoading(false);
      return;
    }

    try {
      const data = await authService.register({
        email: formData.email,
        nickname: formData.nickname,
        password: formData.password,
        birthdate: formData.birthdate || undefined,
        gender: formData.gender || undefined,
        termsAgreed,
        marketingAgreed,
      });

      if (data.requiresVerification) {
        setShowVerificationMessage(true);
      } else {
        navigate('/login');
      }
    } catch (error) {
      setError(error.userMessage || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // 실시간 유효성 검사
    const newFieldErrors = { ...fieldErrors };

    if (name === 'email') {
      const emailError = validateEmail(value);
      if (emailError) {
        newFieldErrors.email = emailError;
      } else {
        delete newFieldErrors.email;
      }
    }

    if (name === 'nickname') {
      const nicknameError = validateNickname(value);
      if (nicknameError) {
        newFieldErrors.nickname = nicknameError;
      } else {
        delete newFieldErrors.nickname;
      }
    }

    if (name === 'password') {
      const passwordError = validatePassword(value);
      if (passwordError) {
        newFieldErrors.password = passwordError;
      } else {
        delete newFieldErrors.password;
      }
    }

    if (name === 'confirmPassword') {
      if (value && value !== formData.password) {
        newFieldErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
      } else {
        delete newFieldErrors.confirmPassword;
      }
    }

    setFieldErrors(newFieldErrors);
  };

  if (showVerificationMessage) {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--wide">
          {/* 성공 아이콘 */}
          <div className="auth-success-icon">
            <Icon name="email" size={48} />
            <div className="auth-success-icon__badge">
              <Icon name="check" size={16} color="white" />
            </div>
          </div>

          <h1 className="auth-success-title">
            인증 메일 발송 완료!
          </h1>

          <p className="auth-success-email">
            <strong>{formData.email}</strong>으로
          </p>
          <p className="auth-success-message">
            인증 메일을 전송했습니다.
          </p>

          <div className="auth-steps-card">
            <h3>
              회원가입 완료 단계
            </h3>
            <ol>
              <li>이메일에서 인증 링크 클릭</li>
              <li>이메일 인증 완료</li>
              <li>로그인 후 서비스 이용</li>
            </ol>
          </div>

          <div className="auth-action-buttons">
            <button
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              로그인 하러가기
            </button>
            <button
              onClick={async () => {
                try {
                  await authService.resendVerification(formData.email);
                  showToast({ type: 'success', message: '인증 메일이 재전송되었습니다.' });
                } catch (error) {
                  showToast({ type: 'error', message: error.userMessage || '재전송 실패' });
                }
              }}
              className="btn-secondary"
            >
              인증 메일 재전송
            </button>
          </div>

          <details className="auth-help-details">
            <summary>
              이메일을 받지 못하셨나요?
            </summary>
            <div>
              <p>• 스팸 메일함을 확인해주세요</p>
              <p>• 메일 수신에 몇 분 소요될 수 있습니다</p>
              <p>• 문제가 지속되면 고객센터로 문의해주세요</p>
            </div>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">MoneyMind</h1>
          <p className="auth-subtitle">회원가입</p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="register-email">
              이메일 주소
            </label>
            <input
              id="register-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={fieldErrors.email ? 'has-error' : ''}
              placeholder="이메일을 입력하세요"
            />
            {fieldErrors.email && (
              <div className="form-error">
                {fieldErrors.email}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="register-nickname">
              닉네임
            </label>
            <input
              id="register-nickname"
              type="text"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              required
              className={fieldErrors.nickname ? 'has-error' : ''}
              placeholder="닉네임을 입력하세요"
            />
            {fieldErrors.nickname && (
              <div className="form-error">
                {fieldErrors.nickname}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="register-password">
              비밀번호
            </label>
            <input
              id="register-password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className={fieldErrors.password ? 'has-error' : ''}
            />
            {formData.password && (
              <PasswordChecklist password={formData.password} />
            )}
            {fieldErrors.password && (
              <div className="form-error">
                {fieldErrors.password}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="register-confirmPassword">
              비밀번호 확인
            </label>
            <input
              id="register-confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className={fieldErrors.confirmPassword ? 'has-error' : ''}
            />
            {fieldErrors.confirmPassword && (
              <div className="form-error">
                {fieldErrors.confirmPassword}
              </div>
            )}
          </div>

          {/* 선택 정보 섹션 (접이식) */}
          <details className="optional-section">
            <summary className="optional-section__header">
              <Icon name="info" size={20} color="var(--color-primary)" outlined />
              <span>선택 정보 (더 정확한 AI 분석을 위해)</span>
            </summary>

            <div className="optional-section__content">
              <div className="form-group">
                <label htmlFor="register-birthdate">
                  생년월일
                </label>
                <input
                  id="register-birthdate"
                  type="date"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleChange}
                  placeholder="YYYY-MM-DD"
                />
              </div>

              <div className="form-group">
                <label>성별</label>
                <div className="gender-buttons">
                  <button
                    type="button"
                    className={`gender-btn ${formData.gender === 'male' ? 'gender-btn--active' : ''}`}
                    onClick={() => handleGenderSelect('male')}
                    aria-pressed={formData.gender === 'male'}
                  >
                    남성
                  </button>
                  <button
                    type="button"
                    className={`gender-btn ${formData.gender === 'female' ? 'gender-btn--active' : ''}`}
                    onClick={() => handleGenderSelect('female')}
                    aria-pressed={formData.gender === 'female'}
                  >
                    여성
                  </button>
                  <button
                    type="button"
                    className={`gender-btn ${formData.gender === 'none' ? 'gender-btn--active' : ''}`}
                    onClick={() => handleGenderSelect('none')}
                    aria-pressed={formData.gender === 'none'}
                  >
                    선택안함
                  </button>
                </div>
              </div>
            </div>
          </details>

          {/* 약관 동의 섹션 */}
          <div className="terms-container">
            {/* 전체 동의 */}
            <div className="terms-all-row">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={allAgreed}
                  onChange={handleAllAgreeToggle}
                  aria-label="전체 동의"
                />
                <span className="terms-all-text">전체 동의</span>
              </label>
            </div>

            <div className="terms-divider" />

            {/* [필수] 서비스 이용약관 */}
            <div className="terms-row">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={() => setTermsAgreed(!termsAgreed)}
                />
                <span>[필수] 서비스 이용약관</span>
              </label>
              <button
                type="button"
                className="terms-link"
                onClick={() => openTermsModal('서비스 이용약관', TERMS_OF_SERVICE)}
                aria-label="서비스 이용약관 보기"
              >
                보기
              </button>
            </div>

            {/* [필수] 개인정보 수집·이용 동의 */}
            <div className="terms-row">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={creditInfoAgreed}
                  onChange={() => setCreditInfoAgreed(!creditInfoAgreed)}
                />
                <span>[필수] 개인정보 수집·이용 동의 (거래내역 포함)</span>
              </label>
              <button
                type="button"
                className="terms-link"
                onClick={() => openTermsModal('개인정보 수집·이용 동의', PERSONAL_INFO_AGREEMENT)}
                aria-label="개인정보 수집·이용 동의 보기"
              >
                보기
              </button>
            </div>

            <div className="terms-divider" />

            {/* [선택] 마케팅 정보 수신 동의 */}
            <div className="terms-row">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={marketingAgreed}
                  onChange={() => setMarketingAgreed(!marketingAgreed)}
                />
                <span>[선택] 마케팅 정보 수신 동의</span>
              </label>
              <button
                type="button"
                className="terms-link"
                onClick={() => openTermsModal('마케팅 정보 수신 동의', MARKETING_CONSENT)}
                aria-label="마케팅 정보 수신 동의 보기"
              >
                보기
              </button>
            </div>

            <div className="terms-divider" />

            {/* 만 14세 이상 */}
            <div className="terms-row">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={ageAgreed}
                  onChange={() => setAgeAgreed(!ageAgreed)}
                />
                <span>만 14세 이상 회원입니다.</span>
              </label>
              <button
                type="button"
                className="terms-link"
                onClick={() => openTermsModal('만 14세 미만 이용 불가 안내', UNDER_14_NOTICE)}
                aria-label="만 14세 이상 안내 보기"
              >
                보기
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !requiredTermsAgreed}
            className="btn-primary btn-full-width"
          >
            {isLoading && (
              <span className="loading-spinner"></span>
            )}
            {isLoading ? '이메일 전송 중...' : '회원가입'}
          </button>
        </form>

        <div className="auth-footer">
          <span className="auth-subtitle">이미 계정이 있으신가요? </span>
          <button
            onClick={() => navigate('/login')}
            className="auth-link"
          >
            로그인
          </button>
        </div>
      </div>

      {/* 약관 모달 */}
      {termsModal.open && (
        <TermsModal
          title={termsModal.title}
          content={termsModal.content}
          onClose={closeTermsModal}
        />
      )}
    </div>
  );
}

export default Register;
